import { AppColorViewModel, AppType } from "client/Api/Apps/common";
import { environmentHttpClient as httpClient } from "client/Api/httpClient";
import { AppImageDisplayInfo } from "client/Api/AppIcon/common";
import { Guid } from "client_legacy/ndaw/guid";
import { DocumentStoreType } from "../Documents/common";
import { UserRequestKind, UserRequestStatus } from "../UserRequests/userRequest";
import { DocumentItemType, ListLocationModel } from "../Documents/SharePoint/common";
import { ReactionKind } from "../Hub/common";
import { UserIdentityModel } from "../UserProfile/service";
import { CommunityKind } from "../Communities/community";

export enum NotificationKind {
	Unknown = 0,
	CrossStoreOperation = 1,
	Maintenance = 2,
	Announcement = 3,
	NewMainAppPromotion = 4,
	AppAccess = 5,
	Custom = 6,
	NewEmail = 7,
	UserRequest = 8,
	Birthday = 9,
	BirthdayWish = 10,
	AnnouncementComment = 11,
	DocumentShared = 12,
	KnowledgeItem = 13,
	Event = 14,
	BirthdayWishReaction = 15,
	AnnouncementReaction = 16,
	AnnouncementCommentReaction = 17,
	EventAttendance = 18,
	AnnouncementCommentMention = 19,
	CommunityPost = 20,
	CommunityPostComment = 21,
	CommunityPostCommentMention = 22,
	CommunityPostReaction = 23,
	CommunityPostCommentReaction = 24,
	EventComment = 25,
	EventCommentReaction = 26,
	EventCommentMention = 27,
	EventReaction = 28,
	KnowledgeItemComment = 29,
	KnowledgeItemCommentReaction = 30,
	KnowledgeItemCommentMention = 31,
	KnowledgeItemReaction = 32,
	Community = 33,
	CommunityMembership = 34
}

export enum NotificationStatus {
	Pending = 0,
	Available = 1,
	Removed = 2
}

type GeneralNotificationViewModelProperties = {
	id: number;
	availableFrom: Date|string;
	availableUntil?: Date|string;
	color: AppColorViewModel;
	appIcon: AppImageDisplayInfo;
	status: Exclude<NotificationStatus, NotificationStatus.Pending>;
	canBeDeletedByUser: boolean;
	isDismissed: boolean;
	isRead: boolean;
	batchId: Guid | null;
	version: number;
};

type NotificationDataViewModel<T> =
	T extends NotificationKind.CrossStoreOperation ? CrossStoreNotificationDataViewModel :
	T extends NotificationKind.Maintenance ? MaintenanceNotificationDataViewModel :
	T extends NotificationKind.Announcement ? AnnouncementNotificationDataViewModel :
	T extends NotificationKind.NewMainAppPromotion ? NewMainAppPromotionNotificationDataViewModel :
	T extends NotificationKind.AppAccess ? AppAccessNotificationDataViewModel :
	T extends NotificationKind.Custom ? CustomNotificationDataViewModel :
	T extends NotificationKind.NewEmail ? NewEmailNotificationDataViewModel :
	T extends NotificationKind.UserRequest ? UserRequestNotificationDataViewModel :
	T extends NotificationKind.Birthday ? BirthdayNotificationDataViewModel :
	T extends NotificationKind.BirthdayWish ? BirthdayWishNotificationDataViewModel :
	T extends NotificationKind.BirthdayWishReaction ? BirthdayWishReactionNotificationDataViewModel :
	T extends NotificationKind.AnnouncementComment ? AnnouncementCommentNotificationDataViewModel :
	T extends NotificationKind.DocumentShared ? DocumentShareNotificationDataViewModel :
	T extends NotificationKind.KnowledgeItem ? KnowledgeItemNotificationDataViewModel :
	T extends NotificationKind.Event ? EventNotificationDataViewModel :
	T extends NotificationKind.AnnouncementCommentReaction ? AnnouncementCommentReactionNotificationDataViewModel :
	T extends NotificationKind.AnnouncementReaction ? AnnouncementReactionNotificationDataViewModel :
	T extends NotificationKind.EventAttendance ? EventAttendanceNotificationDataViewModel :
	T extends NotificationKind.AnnouncementCommentMention ? AnnouncementCommentMentionNotificationDataViewModel :
	T extends NotificationKind.CommunityPost ? CommunityPostNotificationDataViewModel :
	T extends NotificationKind.CommunityPostComment ? CommunityPostCommentNotificationDataViewModel :
	T extends NotificationKind.CommunityPostCommentMention ? CommunityPostCommentMentionNotificationDataViewModel :
	T extends NotificationKind.CommunityPostCommentReaction ? CommunityPostCommentReactionNotificationDataViewModel :
	T extends NotificationKind.CommunityPostReaction ? CommunityPostReactionNotificationDataViewModel :
	T extends NotificationKind.EventComment ? EventCommentNotificationDataViewModel :
	T extends NotificationKind.EventCommentReaction ? EventCommentReactionNotificationDataViewModel :
	T extends NotificationKind.EventCommentMention ? EventCommentMentionNotificationDataViewModel :
	T extends NotificationKind.EventReaction ? EventReactionNotificationDataViewModel :
	T extends NotificationKind.KnowledgeItemComment ? KnowledgeItemCommentNotificationDataViewModel :
	T extends NotificationKind.KnowledgeItemCommentReaction ? KnowledgeItemCommentReactionNotificationDataViewModel :
	T extends NotificationKind.KnowledgeItemCommentMention ? KnowledgeItemCommentMentionNotificationDataViewModel :
	T extends NotificationKind.KnowledgeItemReaction ? KnowledgeItemReactionNotificationDataViewModel :
	T extends NotificationKind.Community ? CommunityNotificationDataViewModel :
	T extends NotificationKind.CommunityMembership ? CommunityMembershipNotificationDataViewModel :
	never;

type NotificationKindSpecification<T extends Exclude<NotificationKind, NotificationKind.Unknown>> = {
	kind: T;
	data: NotificationDataViewModel<T>;
};

export type UnknownNotification = GeneralNotificationViewModelProperties & { kind: NotificationKind.Unknown; };
export type CrossStoreNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.CrossStoreOperation>;
export type MaintenanceNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.Maintenance>;
export type AnnouncementNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.Announcement>;
export type NewMainAppPromotionNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.NewMainAppPromotion>;
export type AppAccessNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.AppAccess>;
export type CustomNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.Custom>;
export type NewEmailNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.NewEmail>;
export type UserRequestNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.UserRequest>;
export type BirthdayNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.Birthday>;
export type BirthdayWishNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.BirthdayWish>;
export type AnnouncementCommentNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.AnnouncementComment>;
export type DocumentSharedNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.DocumentShared>;
export type KnowledgeItemNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.KnowledgeItem>;
export type BirthdayWishReactionNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.BirthdayWishReaction>;
export type EventNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.Event>;
export type AnnouncementCommentReactionNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.AnnouncementCommentReaction>;
export type AnnouncementReactionNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.AnnouncementReaction>;
export type EventAttendanceNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.EventAttendance>;
export type AnnouncementCommentMentionNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.AnnouncementCommentMention>;
export type CommunityPostNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.CommunityPost>;
export type CommunityPostCommentNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.CommunityPostComment>;
export type CommunityPostCommentMentionNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.CommunityPostCommentMention>;
export type CommunityPostCommentReactionNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.CommunityPostCommentReaction>;
export type CommunityPostReactionNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.CommunityPostReaction>;
export type EventReactionNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.EventReaction>;
export type EventCommentNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.EventComment>;
export type EventCommentMentionNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.EventCommentMention>;
export type EventCommentReactionNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.EventCommentReaction>;
export type KnowledgeItemCommentNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.KnowledgeItemComment>;
export type KnowledgeItemCommentReactionNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.KnowledgeItemCommentReaction>;
export type KnowledgeItemCommentMentionNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.KnowledgeItemCommentMention>;
export type KnowledgeItemReactionNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.KnowledgeItemReaction>;
export type CommunityNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.Community>;
export type CommunityMembershipNotification = GeneralNotificationViewModelProperties & NotificationKindSpecification<NotificationKind.CommunityMembership>;

type PendingNotificationViewModel = {
	id: number;
	version: number;
	kind: NotificationKind.Unknown;
	status: NotificationStatus.Pending;
	availableFrom: Date|string;
};

export type AvailableNotificationViewModel =
	| UnknownNotification
	| CrossStoreNotification
	| MaintenanceNotification
	| AnnouncementNotification
	| NewMainAppPromotionNotification
	| AppAccessNotification
	| CustomNotification
	| NewEmailNotification
	| UserRequestNotification
	| BirthdayNotification
	| BirthdayWishNotification
	| BirthdayWishReactionNotification
	| AnnouncementCommentNotification
	| DocumentSharedNotification
	| KnowledgeItemNotification
	| EventNotification
	| AnnouncementCommentReactionNotification
	| AnnouncementReactionNotification
	| EventAttendanceNotification
	| AnnouncementCommentMentionNotification
	| CommunityPostNotification
	| CommunityPostCommentNotification
	| CommunityPostCommentMentionNotification
	| CommunityPostReactionNotification
	| CommunityPostCommentReactionNotification
	| EventReactionNotification
	| EventCommentNotification
	| EventCommentMentionNotification
	| EventCommentReactionNotification
	| KnowledgeItemCommentNotification
	| KnowledgeItemCommentReactionNotification
	| KnowledgeItemCommentMentionNotification
	| KnowledgeItemReactionNotification
	| CommunityNotification
	| CommunityMembershipNotification;

export type NotificationViewModel =
	| PendingNotificationViewModel
	| AvailableNotificationViewModel;

export type NotificationsUpdateRequest = {
	latestLoadedVersion: number | null;
	earliestAvailableFromToCheckForStatusChange: Date | null;
	earliestAvailableUntilToCheckForStatusChange: Date | null;
};

export type NotificationsUpdateViewModel = {
	patch: ReadonlyArray<NotificationViewModel>;
	earliestAvailableFrom: Date|string;
};

export enum MeetingProductKind {
	Teams = 1,
	Zoom = 2,
	JoinMe = 3
}

export type OnlineMeeting = {
	product: MeetingProductKind;
	url: string;
};

export type OngoingOrNextAppointmentClientModel = {
	id: string;
	locations: readonly string[];
	subject: string | null;
	start: string;
	end: string;
	meeting: OnlineMeeting;
	color: AppColorViewModel;
	appIcon: AppImageDisplayInfo;
};

export type VolatileActivitiesClientModel = {
	appointment: OngoingOrNextAppointmentClientModel;
};

export enum MutedActivityFilterKind {
	ByActivityKind = 0,
	ByCustomActivityTag = 1,
	Appointment = 2,
	CustomActivityWithoutTag = 3,
	DailyBriefingEmail = 4,
	WeeklyBriefingEmail = 5
}

export type MutedByKindActivityFilterModel = {
	kind: MutedActivityFilterKind.ByActivityKind;
	activityKind: NotificationKind;
};

export type MutedByCustomTagActivityFilterModel = {
	kind: MutedActivityFilterKind.ByCustomActivityTag;
	tag: string;
};

export type MutedAppointmentActivityFilterModel = {
	kind: MutedActivityFilterKind.Appointment;
};

export type MutedAppointmentDailyBriefingEmailFilterModel = {
	kind: MutedActivityFilterKind.DailyBriefingEmail;
};

export type MutedAppointmentWeeklyBriefingEmailFilterModel = {
	kind: MutedActivityFilterKind.WeeklyBriefingEmail;
};

export type MutedCustomActivityWithoutTagFilterModel = {
	kind: MutedActivityFilterKind.CustomActivityWithoutTag;
};

export type MutedActivityFilterModel =
	| MutedByKindActivityFilterModel
	| MutedByCustomTagActivityFilterModel
	| MutedAppointmentActivityFilterModel
	| MutedCustomActivityWithoutTagFilterModel
	| MutedAppointmentDailyBriefingEmailFilterModel
	| MutedAppointmentWeeklyBriefingEmailFilterModel;

export type MutedActivityFilterCollectionModel = {
	filters: readonly MutedActivityFilterModel[];
	canConfigureEmailFilters: boolean;
	canConfigureCommunityFilters: boolean;
};

export type MutedByKindActivityFilterSaveDataModel = {
	activityKind: NotificationKind;
};

export type MutedByCustomTagActivityFilterSaveDataModel = {
	tag: string;
};

export type MutedByKindActivityFilterSaveModel = {
	kind: MutedActivityFilterKind.ByActivityKind;
	byActivityKindData: MutedByKindActivityFilterSaveDataModel;
};

export type MutedByCustomTagActivityFilterSaveModel = {
	kind: MutedActivityFilterKind.ByCustomActivityTag;
	byCustomActivityTagData: MutedByCustomTagActivityFilterSaveDataModel;
};

export type MutedAppointmentActivityFilterSaveModel = {
	kind: MutedActivityFilterKind.Appointment;
};

export type MutedDailyBriefingEmailActivityFilterSaveModel = {
	kind: MutedActivityFilterKind.DailyBriefingEmail;
};

export type MutedWeeklyBriefingEmailActivityFilterSaveModel = {
	kind: MutedActivityFilterKind.WeeklyBriefingEmail;
};

export type MutedCustomActivityWithoutTagFilterSaveModel = {
	kind: MutedActivityFilterKind.CustomActivityWithoutTag;
};

export type MutedActivityFilterSaveModel =
	| MutedByKindActivityFilterSaveModel
	| MutedByCustomTagActivityFilterSaveModel
	| MutedAppointmentActivityFilterSaveModel
	| MutedCustomActivityWithoutTagFilterSaveModel
	| MutedDailyBriefingEmailActivityFilterSaveModel
	| MutedWeeklyBriefingEmailActivityFilterSaveModel;

export type MutedActivityFilterCollectionSaveModel = {
	filters: readonly MutedActivityFilterSaveModel[];
};

export function loadUpdate(
	request: NotificationsUpdateRequest,
	signal?: AbortSignal
): Promise<NotificationsUpdateViewModel> {
	const {
		latestLoadedVersion,
		earliestAvailableUntilToCheckForStatusChange,
		earliestAvailableFromToCheckForStatusChange
	} = request;

	return httpClient.get<NotificationsUpdateViewModel>(
		"/Notifications/Updates",
		{
			latestLoadedVersion,
			earliestAvailableUntilToCheckForStatusChange: earliestAvailableUntilToCheckForStatusChange?.toISOString() ?? null,
			earliestAvailableFromToCheckForStatusChange: earliestAvailableFromToCheckForStatusChange?.toISOString() ?? null
		},
		{ signal }
	);
}

export function markAllAsRead(signal?: AbortSignal): Promise<void> {
	return httpClient.put<void>(
		"/Notifications/MarkAllAsRead",
		undefined,
		{ signal }
	);
}

export function dismiss(notificationIds: ReadonlyArray<number>): Promise<void> {
	return httpClient.put<void>("/Notifications/Dismiss", { notificationIds });
}

export function getNotification(id: number, signal?: AbortSignal): Promise<NotificationViewModel> {
	return httpClient.get<NotificationViewModel>(`/Notifications/${id}`, undefined, {signal});
}

export function deleteNotification(id: number): Promise<void> {
	return httpClient.delete<void>(`/Notifications/${id}`);
}

export function deleteRange(rangeOfIds: ReadonlyArray<number>): Promise<void> {
	return httpClient.delete<void>("/Notifications/Range/", rangeOfIds);
}

export function deleteAll(): Promise<void> {
	return httpClient.delete<void>("/Notifications");
}

export function getVolatileActivities(utcOffset: string, signal?: AbortSignal): Promise<VolatileActivitiesClientModel> {
	return httpClient.get<VolatileActivitiesClientModel>(
		"/notifications/volatile",
		{ utcOffset },
		{ signal }
	);
}

export function listMuteFilters(signal?: AbortSignal): Promise<MutedActivityFilterCollectionModel> {
	return httpClient.get<MutedActivityFilterCollectionModel>(
		"/Notifications/MuteFilters",
		undefined,
		{ signal }
	);
}

export function setMuteFilters(
	model: MutedActivityFilterCollectionSaveModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.put<void>(
		"/Notifications/MuteFilters",
		model,
		{ signal }
	);
}

export function appendMuteFilters(
	model: MutedActivityFilterCollectionSaveModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"/Notifications/MuteFilters",
		model,
		{ signal }
	);
}

export type SharePointDestination = {
	storeType: DocumentStoreType.SharePoint;
	spSiteUrl: string;
	listId: Guid;
	folderId?: number;
};

export type WebDavDestination = {
	storeType: DocumentStoreType.WebDav;
	serverUrl: string;
	folderId: string;
};

export type CrossStoreDestination = SharePointDestination | WebDavDestination;

export type CrossStoreMoveInProgressNotificationDataViewModel = {
	kind: CrossStoreNotificationKind.MoveInProgress;
	masterJobId: string;
	itemsCount: number;
	destinationName: string;
	destination: SharePointDestination;
};

export type CrossStoreCopyInProgressNotificationDataViewModel = {
	kind: CrossStoreNotificationKind.CopyInProgress;
	masterJobId: string;
	itemsCount: number;
	destinationName: string;
	destination: CrossStoreDestination;
};

export type CrossStoreMoveCancellationInProgressNotificationDataViewModel = {
	kind: CrossStoreNotificationKind.MoveCancellationInProgress;
	itemsCount: number;
	destinationName: string;
	destination: CrossStoreDestination;
};

export type CrossStoreCopyCancellationInProgressNotificationDataViewModel = {
	kind: CrossStoreNotificationKind.CopyCancellationInProgress;
	itemsCount: number;
	destinationName: string;
	destination: CrossStoreDestination;
};

export enum CrossStoreFinishStatus {
	SuccessfullyFinished = 2,
	FinishedWithErrors = 3,
	Failed = 4,
	Cancelled = 5
}

export type CrossStoreMoveFinishedNotificationDataViewModel = {
	kind: CrossStoreNotificationKind.MoveFinished;
	masterJobId: string;
	status: CrossStoreFinishStatus;
	itemsCount: number;
	destinationName: string;
	destination: SharePointDestination;
};

export type CrossStoreCopyFinishedNotificationDataViewModel = {
	kind: CrossStoreNotificationKind.CopyFinished;
	masterJobId: string;
	status: CrossStoreFinishStatus;
	itemsCount: number;
	destinationName: string;
	destination: SharePointDestination;
};

export type CrossStoreNotificationDataViewModel =
	| CrossStoreMoveInProgressNotificationDataViewModel
	| CrossStoreMoveFinishedNotificationDataViewModel
	| CrossStoreCopyInProgressNotificationDataViewModel
	| CrossStoreCopyFinishedNotificationDataViewModel
	| CrossStoreMoveCancellationInProgressNotificationDataViewModel
	| CrossStoreCopyCancellationInProgressNotificationDataViewModel;


export type CrossStoreFinishedNotificationDataViewModel =
	| CrossStoreMoveFinishedNotificationDataViewModel
	| CrossStoreCopyFinishedNotificationDataViewModel;

export enum CrossStoreNotificationKind {
	MoveInProgress = 1,
	MoveFinished = 2,
	CopyInProgress = 3,
	CopyFinished = 4,
	MoveCancellationInProgress = 5,
	CopyCancellationInProgress = 6
}

export enum OperationType {
	Move = 1,
	Copy = 2
}

export enum MaintenanceNotificationKind {
	Regular = 1,
	Updated = 2,
	Cancelled = 3
}

export interface MaintenanceNotificationDataViewModel {
	appName: string;
	appType: AppType;
	message: string;
	start: Date;
	end: Date;
	kind: MaintenanceNotificationKind;
}

export interface AnnouncementNotificationDataViewModel {
	announcementId: number;
	title: string;
}

export interface NewMainAppPromotionNotificationDataViewModel {
	name: string;
	appType: AppType;
}

export enum AppAccessNotificationKind {
	UserAccessGranted = 1,
	UserAndOwnerAccessGranted = 2,
	OwnerAccessGranted = 3
}

export interface AppAccessNotificationDataViewModel {
	appName: string;
	appType: AppType;
	kind: AppAccessNotificationKind;
}

export enum CustomNotificationActionKind {
	Check = 1,
	Cancel = 2,
	Add = 3,
	Open = 4,
	Edit = 5,
	Manage = 6,
	Delete = 7,
	Forward = 8,
	Backward = 9
}

export interface CustomNotificationActionViewModel {
	kind: CustomNotificationActionKind;
	title: string;
	url: string;
}

export interface CustomNotificationDataViewModel {
	title: string;
	url: string;
	tag: string | null;
	actions: CustomNotificationActionViewModel[];
}

export type NewEmailNotificationDataViewModel = {
	exchangeId: string;
	subject: string;
	sender: string;
};

export const enum UserRequestNotificationKind {
	Requested = 1,
	Responded = 2
}

type UserRequestAppItemNotificationDataViewModel = {
	kind: UserRequestKind.AppAccess;
	name: string;
};

type UserRequestItemNotificationDataViewModel = UserRequestAppItemNotificationDataViewModel;

export type UserRequestRequestedNotificationDataViewModel = {
	kind: UserRequestNotificationKind.Requested;
	requestId: number | null;
	item: UserRequestItemNotificationDataViewModel;
	requesterName: string;
};

export type UserRequestRespondedNotificationDataViewModel = {
	kind: UserRequestNotificationKind.Responded;
	requestId: number | null;
	item: UserRequestItemNotificationDataViewModel;
	status: Exclude<UserRequestStatus, UserRequestStatus.Open>;
};

export type UserRequestNotificationDataViewModel =
	| UserRequestRequestedNotificationDataViewModel
	| UserRequestRespondedNotificationDataViewModel;

export type BirthdayNotificationDataViewModel = {
	userId: number;
	firstname: string;
	lastname: string;
	birthday: Date | string;
	avatarId: string;
	isWishSent: boolean;
};

export type BirthdayWishNotificationDataViewModel = {
	birthdayWishId: number | null;
	firstname: string;
	lastname: string;
	avatarId: string;
	wish: string;
	currentUserReaction: ReactionKind;
};

export type AnnouncementCommentNotificationDataViewModel = {
	announcementId: number;
	title: string;
	commentId: number;
	firstname: string;
	lastname: string;
	avatarId: string;
};

export type DocumentShareNotificationDataViewModel = {
	sharingUserName: string | null;
	documentName: string;
	extension: string | null;
	documentType: DocumentItemType;
	documentItemId: number;
	location: ListLocationModel;
};

export interface KnowledgeItemNotificationDataViewModel {
	knowledgeItemId: number;
	title: string;
	isNew: boolean;
}

export type BirthdayWishReactionNotificationDataViewModel = {
	firstname: string;
	lastname: string;
	avatarId: string;
	reaction: ReactionKind;
};

export interface EventNotificationDataViewModel {
	eventId: number;
	title: string;
}

export type AnnouncementCommentReactionNotificationDataViewModel = {
	firstname: string;
	lastname: string;
	avatarId: string;
	announcementId: number;
	commentId: number;
	reactionKind: ReactionKind;
	otherReactionCount: number;
	commentPreviewText: string | null;
	isReply: boolean;
};

export type AnnouncementReactionNotificationDataViewModel = {
	firstname: string;
	lastname: string;
	avatarId: string;
	announcementId: number;
	reactionKind: ReactionKind;
	otherReactionCount: number;
	title: string;
};

export enum EventAttendanceNotificationKind {
	Removed = 1,
	Updated = 2,
	Reminder = 3,
	RemovedAttendee = 4
}

export type EventAttendanceNotificationDataViewModel = {
	eventId: number;
	title: string;
} & (
	{
		kind: Exclude<
			EventAttendanceNotificationKind,
			EventAttendanceNotificationKind.Updated | EventAttendanceNotificationKind.Reminder>;
	} | {
		kind: EventAttendanceNotificationKind.Updated;
		changedProperties: readonly LocalizationKey[];
	} | {
		kind: EventAttendanceNotificationKind.Reminder;
		startDate: Date;
		endDate: Date;
	}
);

export type AnnouncementCommentMentionNotificationDataViewModel = {
	announcementId: number;
	title: string;
	commentId: number;
	firstname: string;
	lastname: string;
	avatarId: string;
};

export type CommunityPostCommentMentionNotificationDataViewModel = {
	postId: number;
	postMessagePreview: string | null;
	commentId: number;
	firstname: string;
	lastname: string;
	avatarId: string;
};

export type CommunityNotificationDataViewModel = {
	communityId: number;
	name: string;
};

export type CommunityMembershipNotificationDataViewModel = {
	communityId: number;
	name: string;
};

export type CommunityPostCommentNotificationDataViewModel = {
	postId: number;
	postMessagePreview: string | null;
	commentId: number;
	firstname: string;
	lastname: string;
	avatarId: string;
};

export type CommunityPostNotificationDataViewModel = {
	postId: number;
	messagePreview: string | null;
	user: UserIdentityModel | null;
	communityKind: CommunityKind;
	communityName: string | null;
};

export type CommunityPostCommentReactionNotificationDataViewModel = {
	user: UserIdentityModel;
	postId: number;
	commentId: number;
	reactionKind: ReactionKind;
	otherReactionCount: number;
	commentPreviewText: string | null;
	isReply: boolean;
};

export type CommunityPostReactionNotificationDataViewModel = {
	user: UserIdentityModel;
	postId: number;
	reactionKind: ReactionKind;
	otherReactionCount: number;
	messagePreview: string | null;
};

export type EventCommentNotificationDataViewModel = {
	eventId: number;
	title: string;
	commentId: number;
	firstname: string;
	lastname: string;
	avatarId: string;
};

export type EventCommentReactionNotificationDataViewModel = {
	firstname: string;
	lastname: string;
	avatarId: string;
	eventId: number;
	commentId: number;
	reactionKind: ReactionKind;
	otherReactionCount: number;
	commentPreviewText: string | null;
	isReply: boolean;
};

export type EventCommentMentionNotificationDataViewModel = {
	eventId: number;
	title: string;
	commentId: number;
	firstname: string;
	lastname: string;
	avatarId: string;
};

export type EventReactionNotificationDataViewModel = {
	firstname: string;
	lastname: string;
	avatarId: string;
	eventId: number;
	reactionKind: ReactionKind;
	otherReactionCount: number;
	title: string;
};

export type KnowledgeItemCommentNotificationDataViewModel = {
	knowledgeItemId: number;
	title: string;
	commentId: number;
	firstname: string;
	lastname: string;
	avatarId: string | null;
};

export type KnowledgeItemCommentReactionNotificationDataViewModel = {
	firstname: string;
	lastname: string;
	avatarId: string | null;
	knowledgeItemId: number;
	commentId: number;
	reactionKind: ReactionKind;
	otherReactionCount: number;
	commentPreviewText: string | null;
	isReply: boolean;
};

export type KnowledgeItemCommentMentionNotificationDataViewModel = {
	knowledgeItemId: number;
	title: string;
	commentId: number;
	firstname: string;
	lastname: string;
	avatarId: string | null;
};

export type KnowledgeItemReactionNotificationDataViewModel = {
	firstname: string;
	lastname: string;
	avatarId: string | null;
	knowledgeItemId: number;
	reactionKind: ReactionKind;
	otherReactionCount: number;
	title: string;
};